import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { fetchHolding } from '@morpho-org/blue-sdk-viem';
import { getChainAddress } from '@morpho-org/morpho-sdk/addresses';
import { isDefined, UnknownAddressError } from '@morpho-org/morpho-ts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { usePublicClient } from 'wagmi';

/** Bundle periphery contracts that can be gated by a token issuer. */
export type BundleKey = 'bundles.blueBundlesV1' | 'bundles.vaultBundlesV1' | 'bundles.vaultExitBundlesV1';

/**
 * Whether the asset's issuer permits the given bundle contract to transfer the
 * asset — the SDK's `canTransfer` mechanism (permissioned-wrapper
 * `hasPermission` / whitelist-aggregator `isWhitelisted`) applied to the
 * bundle address rather than the user's wallet.
 *
 * Non-permissioned assets always return `true`. Returns `true` while unknown
 * or loading so it never blocks the action prematurely.
 *
 * `deployless: false` mirrors `useAssetCanTransferOnchain` so the wallet-side
 * and bundle-side reads exercise the same `fetchHolding` code path.
 *
 * Chains without a registered bundle for `bundleKey` return `true` — other
 * flows already handle an unregistered bundle.
 *
 * @param bundleKey - The bundle periphery to check (`bundles.*` registry label).
 * @param assetAddress - The token the bundle must be able to transfer.
 * @param chainId - The chain the asset lives on.
 */
const useBundleCanTransferOnchain = (bundleKey, assetAddress, chainId) => {
  const $ = _c(20);
  let t0;
  if ($[0] !== chainId) {
    t0 = {
      chainId
    };
    $[0] = chainId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const client = usePublicClient(t0);
  let t1;
  bb0: {
    if (!isDefined(chainId)) {
      t1 = undefined;
      break bb0;
    }
    ;
    try {
      let t3;
      if ($[2] !== bundleKey || $[3] !== chainId) {
        t3 = getChainAddress(chainId, bundleKey);
        $[2] = bundleKey;
        $[3] = chainId;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      t1 = t3;
    } catch (t2) {
      const error = t2;
      if (error instanceof UnknownAddressError) {
        t1 = undefined;
        break bb0;
      }
      throw error;
    }
  }
  const bundleAddress = t1;
  let t2;
  if ($[5] !== assetAddress || $[6] !== bundleKey || $[7] !== chainId) {
    t2 = ["bundle-can-transfer", chainId, bundleKey, assetAddress];
    $[5] = assetAddress;
    $[6] = bundleKey;
    $[7] = chainId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== assetAddress || $[10] !== bundleAddress || $[11] !== client) {
    t3 = () => fetchHolding(bundleAddress, assetAddress, client, {
      deployless: false
    });
    $[9] = assetAddress;
    $[10] = bundleAddress;
    $[11] = client;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = !!bundleAddress && !!assetAddress && !!client;
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      staleTime: 300000
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const {
    data: holding,
    isLoading
  } = useQuery(t5);
  const t6 = holding?.canTransfer ?? true;
  let t7;
  if ($[17] !== isLoading || $[18] !== t6) {
    t7 = {
      canTransfer: t6,
      isLoading
    };
    $[17] = isLoading;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
export default useBundleCanTransferOnchain;

/**
 * Whether a market action is blocked by a missing bundle authorization. Only legs that
 * actually move through the bundle count; with no input yet, any unauthorized leg still
 * surfaces the callout so the user sees it before typing (the CTA is disabled anyway).
 */
export const isMarketBundleBlocked = ({
  legs
}: {
  legs: {
    amount: bigint;
    authorized: boolean;
  }[];
}): boolean => {
  const hasInput = legs.some(leg => leg.amount > 0n);
  return legs.some(leg => !leg.authorized && (!hasInput || leg.amount > 0n));
};